import { compose } from 'recompose';
import { connect } from 'react-redux';
import { withStyles, withTheme } from '@material-ui/core';
import React, { useState } from 'react';

import { Box, Card, CardContent, Tooltip, Typography } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import CrudTable from '../../../clients/details/crudTable.component';
import Spinner from 'modules/common/spinner.component';
import { getHhgSegments, updateHhgEstimate } from 'modules/authorizations/store/actions/houseHoldGoods.actions';
import { UPDATE_HHG_ESTIMATE_FAILURE } from 'modules/authorizations/types';
import { TOAST_MESSAGE_SEVERITY_ERROR } from 'modules/layout/types';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';

const styles = theme => {
  return {
    row: {
      backgroundColor: theme.palette.common.white
    },
    accordionStyleContacts: {
      maxWidth: 1050
    },
    inputGroup: {
      display: 'inline-flex',
      justifyContent: 'space-between'
    },
    root: {
      maxWidth: 170,
      marginRight: 10
    },
    extRoot: {
      width: 115,
      marginRight: 10
    },
    primaryContactRow: {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'space-between',
      width: '100%'
    },
    primaryText: {
      paddingTop: 12,
      paddingRight: 5
    },
    newRow: {
      display: 'flex',
      flexDirection: 'column',
      maxWidth: 1050
    },
    tabBorder: {
      borderLeft: `2px solid ${theme.palette.grey[400]}`,
      marginLeft: theme.spacing(2)
    }
  };
};

const transformEstimates = (estimates, hhgStatuses = []) => {
  if (!Array.isArray(estimates) || estimates.length === 0) return [];

  const getStatusLabel = statusId => {
    if (!statusId) return null;

    return hhgStatuses && Array.isArray(hhgStatuses) ? hhgStatuses.find(s => isIdEqual(s.id, statusId))?.title : '';
  };

  return estimates.filter(Boolean).map(ei => {
    const e = ei?.estimateInfo ?? {};
    const hhgEstimateId = e.hhgEstimateId ?? null;
    const statusId = e.statusId ?? null;

    return {
      hhgEstimateId,
      statusId,
      status: getStatusLabel(statusId),
      estimateName: e.estimateName ?? '',
      orderedDate: e.orderedDate ?? null,
      appointmentDate: e.appointmentDate ?? null,
      receivedDate: e.receivedDate ?? null
    };
  });
};

const EstimateMovingDates = props => {
  const { segmentInfo, hHGServiceStatusTypes, hideEdit, id } = props;
  const hhgStatuses = hHGServiceStatusTypes && Array.isArray(hHGServiceStatusTypes) ? hHGServiceStatusTypes : [];
  const estimates = segmentInfo && segmentInfo.estimates ? segmentInfo.estimates : [];
  const estimatesTransformed = transformEstimates(estimates, hhgStatuses);

  const [isLoading, setIsLoading] = useState(false);

  const adjustedColumns = [
    { accessorKey: 'hhgEstimateId', header: 'Id', type: 'text', hideInModel: true, hideInTable: true, disabled: true },
    { accessorKey: 'status', header: 'Status', type: 'text', required: true, size: 75, disabled: true },
    { accessorKey: 'estimateName', header: 'Estimate Name', type: 'text', required: true, size: 75, disabled: true },
    {
      header: 'Ordered Date',
      accessorKey: 'orderedDate',
      type: 'date',
      disabled: true,
      Cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString('en-us') : '-'),
      size: 65
    },
    {
      header: 'Appointment Date',
      accessorKey: 'appointmentDate',
      type: 'date',
      Cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString('en-us') : '-'),
      size: 65
    },
    {
      header: 'Received Date',
      accessorKey: 'receivedDate',
      type: 'date',
      Cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString('en-us') : '-'),
      size: 65
    }
  ];

  const updateRow = async (movingDate, values) => {
    setIsLoading(true);
    const selectedId = movingDate.original.hhgEstimateId;
    const selected = estimates.find(s => s.estimateInfo.hhgEstimateId === selectedId);
    const infoRequest = { ...selected.estimateInfo };
    infoRequest.orderedDate = values.orderedDate && values.orderedDate !== '' ? values.orderedDate : null;
    infoRequest.appointmentDate = values.appointmentDate && values.appointmentDate !== '' ? values.appointmentDate : null;
    infoRequest.receivedDate = values.receivedDate && values.receivedDate !== '' ? values.receivedDate : null;
    const request = { ...selected, estimateInfo: infoRequest };

    const resp = await props.updateHhgEstimate(id, request);
    if (resp.type === UPDATE_HHG_ESTIMATE_FAILURE) {
      props.showToast('Failed to update the estimate accepted data, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      setIsLoading(false);
      return false;
    } else {
      await props.getHhgSegments(id);
    }
    setIsLoading(false);
    return true;
  };

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
          <Tooltip title={'Moving Dates'} key={'movingDates'} placement="right">
            <FontAwesomeIcon icon={['fas', 'calendar-days']} size="2x" />
          </Tooltip>
          <Typography variant="h5" sx={{ paddingLeft: '20px', fontSize: '1.3125rem', fontWeight: 'bold' }}>
            Moving Dates
          </Typography>
        </Box>
        {!isLoading && (
          <CrudTable
            editingMode={'modal'} // Can be changed to 'row' to edit inline
            columns={adjustedColumns}
            rows={estimatesTransformed}
            updateRow={updateRow}
            hideEdit={hideEdit}
            hideAdd={true}
            hideDelete={true}
          />
        )}
        {isLoading && <Spinner />}
      </CardContent>
    </Card>
  );
};

export default compose(
  withStyles(styles),
  withTheme,
  connect(null, {
    updateHhgEstimate,
    getHhgSegments
  })
)(EstimateMovingDates);
